import React from 'react';
import { Card, Row, Col, Steps, Typography } from 'antd';
import {
  CheckCircleOutlined,
  FileTextOutlined,
  FilterOutlined,
  HomeOutlined,
  MedicineBoxOutlined,
  SettingOutlined,
  ShopOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';

const { Text, Paragraph } = Typography;

export const WIZARD_STEPS = [
  { key: 'general', title: 'Basics', icon: <SettingOutlined /> },
  { key: 'entitlement', title: 'Who', icon: <TeamOutlined /> },
  { key: 'facility-types', title: 'Kinds', icon: <ShopOutlined /> },
  { key: 'facilities', title: 'Sites', icon: <HomeOutlined /> },
  { key: 'tariffs', title: 'Prices', icon: <UnorderedListOutlined /> },
  { key: 'categories', title: 'Services', icon: <MedicineBoxOutlined /> },
  { key: 'items', title: 'Procedures', icon: <FilterOutlined /> },
  { key: 'workflow', title: 'Documents', icon: <FileTextOutlined /> },
  { key: 'preview', title: 'Test', icon: <ThunderboltOutlined /> },
];

export const POLICY_MODE_CARDS = [
  {
    value: 'open',
    title: 'No restriction',
    example: 'Use whatever the facility already has set up.',
  },
  {
    value: 'include',
    title: 'Only these',
    example: 'Staff can pick only from your list below.',
  },
  {
    value: 'exclude',
    title: 'All except these',
    example: 'Everything is allowed except what you block below.',
  },
];

export const ENTITLEMENT_CARDS = [
  {
    value: 'generic',
    title: 'Any member',
    example: 'Anyone with an active enrolment can use this type.',
  },
  {
    value: 'category_required',
    title: 'Members with a plan benefit',
    example: 'Only if their plan includes the benefit you pick (e.g. Dental).',
  },
];

export const EVIDENCE_CARDS = [
  { value: 'not_needed', title: 'Skip', example: 'No file needed.' },
  { value: 'optional', title: 'Nice to have', example: 'Can upload, but not required.' },
  { value: 'required', title: 'Must upload', example: 'PA cannot finish until file is attached.' },
];

export function SetupWizardNav({ activeKey, onChange, isNew }) {
  const current = WIZARD_STEPS.findIndex((s) => s.key === activeKey);
  return (
    <Card size="small" style={{ marginBottom: 16 }}>
      {isNew && (
        <Paragraph type="secondary" style={{ marginBottom: 12, fontSize: 13 }}>
          Start with <Text strong>Basics</Text> and save to create the type. Then work left to right — each step builds on the previous one.
        </Paragraph>
      )}
      <Steps
        size="small"
        current={current < 0 ? 0 : current}
        onChange={(idx) => onChange?.(WIZARD_STEPS[idx]?.key)}
        items={WIZARD_STEPS.map((s) => ({ title: s.title, icon: s.icon }))}
        style={{ cursor: 'pointer' }}
      />
    </Card>
  );
}

export function ChoiceCards({ value, onChange, options, columns = 3 }) {
  const colSpan = columns === 2 ? 12 : 8;
  return (
    <Row gutter={[12, 12]} role="radiogroup">
      {options.map((opt) => {
        const selected = value === opt.value;
        return (
          <Col xs={24} md={colSpan} key={opt.value}>
            <Card
              size="small"
              hoverable
              onClick={() => onChange?.(opt.value)}
              style={{
                cursor: 'pointer',
                borderColor: selected ? '#1677ff' : '#d9d9d9',
                borderWidth: selected ? 2 : 1,
                background: selected ? '#e6f4ff' : '#fff',
              }}
            >
              <Text strong style={{ display: 'block', marginBottom: 4 }}>{opt.title}</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>{opt.example}</Text>
            </Card>
          </Col>
        );
      })}
    </Row>
  );
}

export function PolicyModePicker({ value, onChange, noun = 'items' }) {
  const options = POLICY_MODE_CARDS.map((c) => ({
    ...c,
    example: c.value === 'include' && noun
      ? `Only the ${noun} you add below.`
      : c.value === 'exclude' && noun
        ? `All ${noun} except those you block below.`
        : c.example,
  }));
  return <ChoiceCards value={value} onChange={onChange} options={options} />;
}

export function EntitlementModePicker({ value, onChange }) {
  return <ChoiceCards value={value} onChange={onChange} options={ENTITLEMENT_CARDS} columns={2} />;
}

export function EvidenceModePicker({ value, onChange }) {
  return <ChoiceCards value={value} onChange={onChange} options={EVIDENCE_CARDS} />;
}

export function LiveSummary({ children, type = 'success' }) {
  if (!children) return null;
  return (
    <Card
      size="small"
      style={{
        marginBottom: 16,
        background: type === 'warning' ? '#fffbe6' : '#f6ffed',
        borderColor: type === 'warning' ? '#ffe58f' : '#b7eb8f',
      }}
    >
      <Text>
        <CheckCircleOutlined style={{ color: type === 'warning' ? '#faad14' : '#52c41a', marginRight: 8 }} />
        <Text strong>Right now: </Text>
        {children}
      </Text>
    </Card>
  );
}

export function NextStepHint({ nextTabKey }) {
  const next = WIZARD_STEPS.find((s) => s.key === nextTabKey);
  if (!next) return null;
  return (
    <Paragraph type="secondary" style={{ fontSize: 13, marginTop: 16, marginBottom: 0 }}>
      Next: open the <Text strong>{next.title}</Text> step when you are done here.
    </Paragraph>
  );
}

/** Plain-English one-liner for policy sections. */
export function describePolicyMode(mode, ids = [], labels = [], noun = 'items') {
  const count = ids?.length ?? 0;
  const sample = labels?.slice(0, 2).join(', ');
  const more = count > 2 ? ` and ${count - 2} more` : '';
  if (mode === 'open') return `Any ${noun} — no list applied.`;
  if (mode === 'include') {
    if (count === 0) return `Include mode but list is empty — effectively all ${noun} (until you add some).`;
    return `Only ${count} ${noun}: ${sample}${more}.`;
  }
  if (mode === 'exclude') {
    if (count === 0) return `Exclude mode but nothing blocked — all ${noun} allowed.`;
    return `All ${noun} except ${count} blocked: ${sample}${more}.`;
  }
  return null;
}
